import { AxiosResponse } from 'axios'
import { Dayjs } from 'dayjs'

import { _axios } from 'hooks/axios'
import { NewDate } from 'hooks/helper/date'
import { Assign, AllianceAssign, GetHistoryResponse } from 'types/assign'
import { AssignFile } from 'types/assignFile'
import { NotificationFileInfo } from 'types/notification'
import { OrderWithAssign } from 'types/order'

const UPDATE_SECURITY_NOTE = (id: string) => `/assigns/${id}/security-note`
const FIND_BY_ID = (id: string) => `/assigns/${id}`
const FIND_DETAIL_BY_ID = (id: string) => `/assigns/${id}/detail`
const UPDATE_STATUS = (id: string) => `/assigns/${id}`
const DOWN = (id: string) => `/assigns/${id}/down`
const UPDATE_SECURITY_REVIEW = (id: string) => `/assigns/${id}/review`
const UPDATE_SECURITY_REVIEW_REASONE = (id: string) =>
  `/assigns/${id}/review/reasone`
const UPDATE_ASSIGN_REVIEW = (id: string) => `/assigns/${id}/assign-review`
const GET_IMAGES = (id: string) => `/assigns/${id}/images`
const DELETE_IMAGE = (id: string, assignFileId: string) =>
  `/assigns/${id}/assign-file/${assignFileId}`
const IMAGE_SECURITY_REPORT = (id: string) =>
  `/assigns/${id}/images/security-report`
const GET_PRESIGNED_URL = (id: string) => `/assigns/${id}/images/presigned-url`
const COMPLETE_IMAGE_UPLOAD = (id: string) => `/assigns/${id}/images/complete`
const GET_HISTORY = '/assigns/history'

export interface FindDetailByIdResponse {
  volume: number
  qualifiedVolume: number
  nonQualifiedVolume: number
  order: OrderWithAssign
  otherAssigns: Assign[]
  otherAllianceAssigns: AllianceAssign[]
  isLeader: boolean
  notificationFiles: NotificationFileInfo[]
}

export const assignApi = () => {
  const findById = async (id: string): Promise<AxiosResponse<Assign>> => {
    return await _axios()
      .get(FIND_BY_ID(id))
      .then((res) => {
        res.data = res.data.assign
        return res
      })
  }

  const findDetailById = async (
    id: string,
  ): Promise<AxiosResponse<FindDetailByIdResponse>> => {
    return await _axios()
      .get(FIND_DETAIL_BY_ID(id))
      .then((res) => {
        const order: OrderWithAssign = res.data.order
        order.date = NewDate(order.date)
        order.startTime = NewDate(order.startTime)
        order.endTime = NewDate(order.endTime)
        res.data.order = order
        return res
      })
  }

  const updateStatus = async (
    id: string,
    status: number,
    beginCondition: number,
    beginConditionNote?: string,
  ): Promise<AxiosResponse<string>> => {
    const params = {
      status: status,
      beginCondition: beginCondition,
      beginConditionNote: beginConditionNote,
    }
    return await _axios()
      .put(UPDATE_STATUS(id), params)
      .then((res) => {
        res.data = res.data
        return res
      })
  }

  const down = async (
    id: string,
    endTime: Dayjs,
  ): Promise<AxiosResponse<string>> => {
    const params = {
      endTime: endTime,
    }
    return await _axios()
      .put(DOWN(id), params)
      .then((res) => {
        res.data = res.data
        return res
      })
  }

  const updateSecurityNote = async (
    id: string,
    note: string,
  ): Promise<AxiosResponse<string>> => {
    const params = {
      securityNote: note,
    }
    return await _axios()
      .put(UPDATE_SECURITY_NOTE(id), params)
      .then((res) => {
        res.data = res.data
        return res
      })
  }

  const updateSecurityReview = async (
    id: string,
    review: number,
    assignReviewNote: string,
  ): Promise<AxiosResponse<string>> => {
    const params = {
      review: review,
      assignReviewNote: assignReviewNote,
    }
    return await _axios()
      .put(UPDATE_SECURITY_REVIEW(id), params)
      .then((res) => {
        res.data = res.data
        return res
      })
  }

  const updateSecurityReviewReasone = async (
    id: string,
    reasone: string,
  ): Promise<AxiosResponse<string>> => {
    const params = {
      reasone: reasone,
    }
    return await _axios()
      .put(UPDATE_SECURITY_REVIEW_REASONE(id), params)
      .then((res) => {
        res.data = res.data
        return res
      })
  }

  const updateAssignReview = async (
    id: string,
    assignReviews: { category: number; version: number; value: number }[],
  ): Promise<AxiosResponse<string>> => {
    const params = {
      reviews: assignReviews,
    }
    return await _axios()
      .put(UPDATE_ASSIGN_REVIEW(id), params)
      .then((res) => {
        res.data = res.data
        return res
      })
  }

  const getImages = async (
    id: string,
  ): Promise<AxiosResponse<{ imageUrls: AssignFile[] }>> => {
    return await _axios().get(GET_IMAGES(id))
  }

  const deleteImage = async (
    id: string,
    assignFileId: string,
  ): Promise<AxiosResponse<string>> => {
    return await _axios().delete(DELETE_IMAGE(id, assignFileId))
  }

  const submitImage = async (id: string): Promise<AxiosResponse<string>> => {
    return await _axios().post(IMAGE_SECURITY_REPORT(id))
  }

  const getPresignedUrl = async (
    id: string,
    filename: string,
    contentType: string,
  ): Promise<
    AxiosResponse<{
      presignedUrl: string
      fileId: string
      key: string
    }>
  > => {
    const params = {
      filename,
      contentType,
    }
    return await _axios().post(GET_PRESIGNED_URL(id), params)
  }

  const uploadToS3 = async (
    presignedUrl: string,
    file: File,
  ): Promise<void> => {
    // NOTE: S3への直接アップロードのためfetchを使用
    // axiosのインターセプター（認証トークン等）を避けるため
    const response = await fetch(presignedUrl, {
      method: 'PUT',
      body: file,
    })

    if (!response.ok) {
      throw new Error('S3アップロード失敗')
    }
  }

  const completeImageUpload = async (
    id: string,
    fileId: string,
    key: string,
  ): Promise<AxiosResponse<{ imageUrl: string }>> => {
    const params = {
      fileId,
      key,
    }
    return await _axios().post(COMPLETE_IMAGE_UPLOAD(id), params)
  }

  const uploadImageWithPresignedUrl = async (
    id: string,
    file: File,
  ): Promise<AxiosResponse<{ imageUrl: string }>> => {
    // Step 1: presigned URL取得
    const presignedResponse = await getPresignedUrl(id, file.name, file.type)
    const { presignedUrl, fileId, key } = presignedResponse.data

    // Step 2: S3へ直接アップロード
    await uploadToS3(presignedUrl, file)

    // Step 3: アップロード完了通知
    return await completeImageUpload(id, fileId, key)
  }

  const getHistory = async (
    contractDetailId: string,
  ): Promise<AxiosResponse<GetHistoryResponse>> => {
    return await _axios().get(GET_HISTORY, {
      params: { contract_detail_id: contractDetailId },
    })
  }

  return {
    findById,
    findDetailById,
    updateStatus,
    updateSecurityNote,
    updateSecurityReview,
    updateSecurityReviewReasone,
    updateAssignReview,
    getImages,
    deleteImage,
    submitImage,
    down,
    getPresignedUrl,
    uploadToS3,
    completeImageUpload,
    uploadImageWithPresignedUrl,
    getHistory,
  }
}
